import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../lib/axios';
import type { Product } from '../../types';
import { ProductInventorySection } from './components/ProductInventorySection';
import { ProductStockHistorySection } from './components/ProductStockHistorySection';
import { QuickStockAdjustmentDialog } from './components/QuickStockAdjustmentDialog';

export const ProductShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [preselectedLocationId, setPreselectedLocationId] = useState<string | undefined>(undefined);

  const handleOpenAdjustment = (locationId: string) => {
    setPreselectedLocationId(locationId);
    setAdjustmentDialogOpen(true);
  };

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await axiosInstance.get<Product>(`/products/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load product'}
        </Alert>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box>
        <Alert severity="info">Product not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Product Details</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/products')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/app/products/edit/${id}`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Stack gap={3}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Product Name
              </Typography>
              <Typography variant="body1">{product.name}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1">{product.description || 'N/A'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Category
              </Typography>
              <Typography variant="body1">{product.category || 'N/A'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Price
              </Typography>
              <Typography variant="body1">{formatCurrency(product.price)}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                SKU
              </Typography>
              <Typography variant="body1">{product.sku || 'N/A'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Box>
                <Chip
                  label={product.isActive ? 'Active' : 'Inactive'}
                  color={product.isActive ? 'success' : 'default'}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created At
              </Typography>
              <Typography variant="body1">
                {new Date(product.createdAt).toLocaleString('fr-DZ')}
              </Typography>
            </Box>

            {(product as any).attributes &&
              Object.keys((product as any).attributes).length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Attributes
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {Object.entries((product as any).attributes).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${value}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Variants Section */}
      {product.variants && product.variants.length > 0 && (
        <Box mt={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Product Variants</Typography>
                <Chip
                  label={`${product.variants.length} variant${product.variants.length !== 1 ? 's' : ''}`}
                  color="primary"
                  size="small"
                />
              </Box>

              <Stack gap={2}>
                {product.variants.map((variant) => (
                  <Card key={variant.id} variant="outlined">
                    <CardContent>
                      <Stack gap={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="bold">
                            {variant.sku}
                          </Typography>
                          <Chip
                            label={variant.isActive ? 'Active' : 'Inactive'}
                            color={variant.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>

                        {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Attributes
                            </Typography>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {Object.entries(variant.attributes).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {variant.priceAdjustment !== 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Price Adjustment
                            </Typography>
                            <Typography variant="body2" color={variant.priceAdjustment > 0 ? 'success.main' : 'error.main'}>
                              {variant.priceAdjustment > 0 ? '+' : ''}
                              {formatCurrency(variant.priceAdjustment)}
                            </Typography>
                          </Box>
                        )}

                        {variant.images && variant.images.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Images
                            </Typography>
                            <Typography variant="body2">
                              {variant.images.length} image{variant.images.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Inventory Section */}
      <Box mt={3}>
        <ProductInventorySection productId={id!} onAdjustStock={handleOpenAdjustment} />
      </Box>

      {/* Stock History Section */}
      <Box mt={3}>
        <ProductStockHistorySection productId={id!} />
      </Box>

      {/* Quick Stock Adjustment Dialog */}
      <QuickStockAdjustmentDialog
        open={adjustmentDialogOpen}
        onClose={() => {
          setAdjustmentDialogOpen(false);
          setPreselectedLocationId(undefined);
        }}
        productId={id!}
        preselectedLocationId={preselectedLocationId}
      />
    </Box>
  );
};
